import React, { useState, useEffect } from 'react';
import { getPermisos, getRolPermisos, assignPermisoToRol, removePermisoFromRol } from '../services/api';
import { useToast } from '../components/Toast';
import { Skeleton } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';

function Permisos() {
  const toast = useToast();
  const [allPermisos, setAllPermisos] = useState([]);
  const [rolPermisos, setRolPermisos] = useState([]);
  const [selectedRol, setSelectedRol] = useState('vecino');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPermiso, setSelectedPermiso] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    cargarDatos();
  }, [selectedRol]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [permisosRes, rolPermisosRes] = await Promise.all([
        getPermisos(),
        getRolPermisos(selectedRol)
      ]);
      setAllPermisos(permisosRes.data);
      setRolPermisos(rolPermisosRes.data);
    } catch (error) {
      toast.error('Error al cargar permisos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAsignarPermiso = async () => {
    if (!selectedPermiso) {
      toast.warning('Selecciona un permiso');
      return;
    }

    try {
      await assignPermisoToRol(selectedRol, selectedPermiso);
      toast.success('Permiso asignado correctamente');
      setShowAddModal(false);
      setSelectedPermiso('');
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al asignar permiso');
    }
  };

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  const handleRemoverPermiso = (asignacionId, nombrePermiso) => {
    openConfirm(
      'Remover permiso',
      `¿Estás seguro de remover el permiso "${nombrePermiso}" del rol ${selectedRol}?`,
      async () => {
        closeConfirm();
        try {
          await removePermisoFromRol(selectedRol, asignacionId);
          toast.success('Permiso removido correctamente');
          cargarDatos();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al remover permiso');
        }
      }
    );
  };

  const permisosNoAsignados = allPermisos.filter(p =>
    !rolPermisos.some(rp => rp.id === p.id)
  );

  const permisosFiltrados = rolPermisos.filter(p =>
    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.recurso.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const agruparPorRecurso = (permisos) => {
    return permisos.reduce((acc, permiso) => {
      if (!acc[permiso.recurso]) {
        acc[permiso.recurso] = [];
      }
      acc[permiso.recurso].push(permiso);
      return acc;
    }, {});
  };

  const permisosAgrupados = agruparPorRecurso(permisosFiltrados);

  const getAccionColor = (accion) => {
    const colors = {
      crear: '#10b981',
      leer: '#3b82f6',
      editar: '#f59e0b',
      eliminar: '#ef4444',
      gestionar: '#8b5cf6'
    };
    return colors[accion] || '#6b7280';
  };

  const getRolBadgeColor = (rol) => {
    const colors = {
      administrador: '#8b5cf6',
      presidente: '#3b82f6',
      vecino: '#10b981',
      proveedor: '#16a34a',
      conserje: '#f59e0b'
    };
    return colors[rol] || '#6b7280';
  };

  return (
    <div className="container" style={{ paddingTop: '40px', maxWidth: '1200px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
          🔐 Gestión de Permisos
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
          Administra los permisos de cada rol en el sistema
        </p>
      </div>

      {/* Selector de Rol */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: '600', fontSize: '14px' }}>Seleccionar Rol:</span>
          {['administrador', 'presidente', 'vecino', 'proveedor', 'conserje'].map(rol => (
            <button
              key={rol}
              onClick={() => setSelectedRol(rol)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: selectedRol === rol ? '2px solid' : '2px solid transparent',
                borderColor: selectedRol === rol ? getRolBadgeColor(rol) : 'var(--border)',
                background: selectedRol === rol ? `${getRolBadgeColor(rol)}15` : 'var(--bg-elevated)',
                color: selectedRol === rol ? getRolBadgeColor(rol) : 'var(--text-primary)',
                fontWeight: selectedRol === rol ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '14px',
                textTransform: 'capitalize'
              }}
            >
              {rol}
            </button>
          ))}
        </div>
      </div>

      {/* Barra de acciones */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Buscar permisos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-control"
          style={{ maxWidth: '400px' }}
        />
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
          disabled={selectedRol === 'administrador'}
        >
          ➕ Asignar Permiso
        </button>
      </div>

      {selectedRol === 'administrador' && (
        <div style={{
          padding: '16px',
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>ℹ️</span>
          <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            Los administradores tienen todos los permisos por defecto y no pueden ser modificados.
          </span>
        </div>
      )}

      {/* Lista de permisos */}
      <div className="card">
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600' }}>
            Permisos asignados a {selectedRol}
          </h2>
          <span style={{
            padding: '6px 12px',
            background: getRolBadgeColor(selectedRol),
            color: 'white',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            {rolPermisos.length} permisos
          </span>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height="60px" />
            ))}
          </div>
        ) : Object.keys(permisosAgrupados).length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--text-secondary)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <p>No se encontraron permisos</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {Object.entries(permisosAgrupados).map(([recurso, permisos]) => (
              <div key={recurso}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  textTransform: 'capitalize',
                  color: 'var(--primary)'
                }}>
                  📋 {recurso}
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '12px'
                }}>
                  {permisos.map(permiso => (
                    <div
                      key={permiso.asignacion_id}
                      style={{
                        padding: '12px',
                        border: '2px solid var(--border)',
                        borderRadius: '8px',
                        background: 'var(--bg-elevated)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = getAccionColor(permiso.accion);
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border-color)';
                        e.currentTarget.style.background = 'var(--bg-elevated)';
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{
                            padding: '4px 8px',
                            background: getAccionColor(permiso.accion),
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {permiso.accion}
                          </span>
                          <span style={{ fontWeight: '600', fontSize: '14px' }}>
                            {permiso.nombre}
                          </span>
                        </div>
                        {permiso.descripcion && (
                          <p style={{
                            fontSize: '12px',
                            color: 'var(--text-secondary)',
                            margin: '4px 0 0 0'
                          }}>
                            {permiso.descripcion}
                          </p>
                        )}
                        <p style={{
                          fontSize: '11px',
                          color: '#9ca3af',
                          margin: '4px 0 0 0',
                          fontFamily: 'monospace'
                        }}>
                          {permiso.codigo}
                        </p>
                      </div>
                      {selectedRol !== 'administrador' && (
                        <button
                          onClick={() => handleRemoverPermiso(permiso.asignacion_id, permiso.nombre)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '20px',
                            padding: '4px',
                            marginLeft: '8px',
                            opacity: 0.6,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = '1'}
                          onMouseLeave={(e) => e.target.style.opacity = '0.6'}
                          title="Remover permiso"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal para asignar permiso */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowAddModal(false)}>
          <div
            className="card"
            style={{ maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
              Asignar Permiso a {selectedRol}
            </h2>

            {permisosNoAsignados.length === 0 ? (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: 'var(--text-secondary)'
              }}>
                <p>Todos los permisos ya están asignados a este rol</p>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Seleccionar Permiso</label>
                  <select
                    className="form-control"
                    value={selectedPermiso}
                    onChange={(e) => setSelectedPermiso(e.target.value)}
                  >
                    <option value="">Selecciona un permiso...</option>
                    {permisosNoAsignados.map(permiso => (
                      <option key={permiso.id} value={permiso.id}>
                        [{permiso.recurso}] {permiso.nombre} ({permiso.accion})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedPermiso && (
                  <div style={{
                    padding: '12px',
                    background: 'var(--bg-elevated)',
                    borderRadius: '8px',
                    marginTop: '12px'
                  }}>
                    {(() => {
                      const permiso = allPermisos.find(p => p.id === parseInt(selectedPermiso));
                      return permiso ? (
                        <>
                          <div style={{ marginBottom: '8px' }}>
                            <span style={{
                              padding: '4px 8px',
                              background: getAccionColor(permiso.accion),
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase'
                            }}>
                              {permiso.accion}
                            </span>
                          </div>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {permiso.nombre}
                          </div>
                          {permiso.descripcion && (
                            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                              {permiso.descripcion}
                            </div>
                          )}
                        </>
                      ) : null;
                    })()}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="btn btn-outline"
                    style={{ flex: 1 }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAsignarPermiso}
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    disabled={!selectedPermiso}
                  >
                    Asignar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel="Eliminar"
        variant="danger"
      />
    </div>
  );
}

export default Permisos;
